/* eslint-disable no-plusplus,no-param-reassign */
import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import { combineTextrunStyle, getTextLength, textRunStyleMerge } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import { ITextRunStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { ITextRun } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { stylesToMark } from '@src/framework/editor/src/page/index/components/editor/data/mark';
import { deepCopy } from '@src/slide/infrastructure/module/independent/object-operation';

const getTextRunLength = (textRun: ITextRun): number => (textRun.text ? textRun.text.length : 0);

export function getEmptyTextrun(endParaRPr, resizeLevel) {
    return {
        text: '',
        marks: stylesToMark(combineTextrunStyle({
            textRunStyle: endParaRPr,
            resizeLevel,
        })),
    };
}

const textOffsetInSelection = (
    textOffset: number,
    l: number,
    start: number,
    end: number,
): boolean => textOffset >= start && textOffset + l <= end;

/**
 * textRuns分块
 * @param {*} textRuns
 * @param {*} start 起始的文字点位置
 * @param {*} end 结束的文字点位置
 * @returns {*} return.textRuns 新的textruns
 * @returns {*} return.startIndex start的textRunIndex
 * @returns {*} return.endIndex end的textRunIndex
 */
export function splitTextRuns(textRuns: ITextRun[] = [], start: number, end: number): {
    startIndex: number | null;
    endIndex: number | null;
    textRuns: ITextRun[];
} {
    let textOffset = 0;
    let runOffset = 0;
    let startIndex: number | null = null;
    let endIndex: number | null = null;

    // 检查是否有不可拆分的textRun
    const { newStart, newLength } = checkDisableSplitRun(textRuns, start, end - start);
    start = newStart;
    end = newStart + newLength;

    if (start === end) {
        return {
            startIndex,
            endIndex,
            textRuns,
        };
    }

    const t = textRuns.reduce((prev: ITextRun[], textRun) => {
        const l = getTextRunLength(textRun);

        // 还未到选区
        if (textOffset + l <= start) {
            textOffset += l;
            runOffset += 1;
            return prev.concat(textRun);
        }

        // 选区已结束
        if (textOffset >= end) {
            if (expect(endIndex).isNullOrUndefined()) endIndex = runOffset - 1; // 第一个结束点设定为endIndex
            textOffset += l;
            runOffset += 1;
            return prev.concat(textRun);
        }

        // 包裹在选区中
        if (textOffsetInSelection(textOffset, l, start, end)) {
            textOffset += l;
            if (expect(startIndex).isNullOrUndefined()) startIndex = runOffset;
            runOffset += 1;
            return prev.concat(textRun);
        }

        // 需要切割
        const s = Math.max(textOffset, start);
        const e = Math.min(end, textOffset + l);
        const nTextRuns: ITextRun[] = [];

        if (s > textOffset) {
            // 起始点push
            nTextRuns.push({
                ...deepCopy(textRun),
                text: textRun.text?.slice(0, s - textOffset),
            });
            runOffset += 1;
        }

        if (expect(startIndex).isNullOrUndefined()) startIndex = runOffset; // 第一个起始点设为startIndex

        nTextRuns.push({
            ...deepCopy(textRun),
            text: textRun.text?.slice(s - textOffset, e - textOffset),
        });
        runOffset += 1;

        if (e < textOffset + l) {
            nTextRuns.push({
                ...deepCopy(textRun),
                text: textRun.text?.slice(e - textOffset, l),
            });
            if (expect(endIndex).isNullOrUndefined()) endIndex = runOffset - 1;
            runOffset += 1;
        }

        textOffset += l;

        return prev.concat(nTextRuns);
    }, []);
    if (expect(endIndex).isNullOrUndefined()) endIndex = t.length && t.length - 1;

    return {
        startIndex,
        endIndex,
        textRuns: t,
    };
}

/**
 * 检查是否有不可分割的textRun
 * @param textRuns
 * @param index
 * @param length
 * @returns {{newLength: number, newStart: number}}
 */
function checkDisableSplitRun(
    textRuns: ITextRun[],
    index: number,
    length: number,
): { newStart: number; newLength: number } {
    let newStart = index;
    let newLength = length;
    if (length <= 0) return { newStart, newLength };
    const startIndex = index;
    const {
        run: runS, startOffset: startOffsetS, endOffset: endOffsetS,
    } = searchTextRunAtOffset(textRuns, startIndex);
    if (isInDisableSplitRun(runS, startIndex, startOffsetS, endOffsetS)) {
        newStart = endOffsetS;
        newLength = Math.max(0, newLength - (newStart - index));
    }

    const endIndex = startIndex + length;
    const {
        run: runE, startOffset: startOffsetE, endOffset: endOffsetE,
    } = searchTextRunAtOffset(textRuns, endIndex);
    if (isInDisableSplitRun(runE, endIndex, startOffsetE, endOffsetE)) {
        newLength = Math.max(0, startOffsetE - startIndex);
    }
    return { newStart, newLength };
}

function isInDisableSplitRun(
    run: ITextRun | undefined,
    index: number,
    startOffset: number,
    endOffset: number,
): boolean {
    return hasDisableSplitRun(run) && index > startOffset && index < endOffset;
}

function hasDisableSplitRun(run: ITextRun | undefined): boolean {
    return !!run?.fieldType;  // 含有fieldType的类型不可分割
}

/**
 * 查找指定位置的textRun
 * @param textRuns
 * @param offset
 * @returns {{endOffset: number, startOffset: number, index: number, run: *}}
 */
function searchTextRunAtOffset(textRuns: ITextRun[], offset: number): {
    endOffset: number;
    startOffset: number;
    index: number;
    run: ITextRun | undefined;
} {
    let endOffset = 0;
    let startOffset = 0;
    let index = -1;

    const run = textRuns.find((l) => {
        index += 1;
        startOffset = endOffset;
        endOffset = startOffset + (l.text?.length || 0);
        return endOffset >= offset;
    });

    return {
        run,
        endOffset,
        index,
        startOffset,
    };
}

export function mergeTextRunsStyle(
    textRuns: ITextRun[],
    textRunStyle: ITextRunStyle,
    start: number, end: number,
): ITextRun[] {
    if (getTextLength({ textRuns }) === 0) {
        // 空文本
        return textRuns.map(textRun => ({
            ...textRun,
            styles: textRunStyleMerge(textRun?.styles, textRunStyle),
        })) || textRuns;
    }

    if (start === end) return textRuns;

    const sTextRuns = splitTextRuns(textRuns, start, end);
    const tR = sTextRuns.textRuns;
    const s = sTextRuns.startIndex;
    const e = sTextRuns.endIndex;
    if (s === null || e === null) {
        return tR;
    }
    for (let i = s; i <= e; i++) {
        tR[i] = {
            ...tR[i],
            styles: textRunStyleMerge(tR[i]?.styles, textRunStyle),
        };
    }

    return tR;
}


export const testTable = {
    checkDisableSplitRun,
};
